<script setup>
import SidenavItem from "./SidenavItem.vue";
import SidenavCollapse from "./SidenavCollapse.vue";
import allowed from "@/services/allowed";
import allowedShowDevFeatures from "@/services/allowedShowDevFeatures";
import api from "@/services/api";

import Administrator from "@/assets/iconsSidebar/Administrator.png";
import Spa from "@/components/icons/Spa.vue";
import Instagram from "@/components/icons/Instagram.vue";
import telegram from "@/assets/iconsSidebar/telegram.png";
import checkUserRole from "@/services/checkUserRole";
import {
  computed,
  defineAsyncComponent,
  onMounted,
  ref,
  useTemplateRef,
} from "vue";
import { useRoute, useRouter } from "vue-router";

const SelectApp = defineAsyncComponent(
  () => import("@/components/common/SelectApp.vue"),
);
import { useStore } from "vuex";
import { onClickOutside, useWindowSize } from "@vueuse/core";
import { useUiStore } from "@/storePinia/ui";
import { useCompanyStore } from "@/storePinia/company";
import Clients from "@/components/icons/Clients.vue";
import CalendarCheck from "@/components/icons/CalendarCheck.vue";
import SackDollar from "@/components/icons/SackDollar.vue";
import ChartBar from "@/components/icons/ChartBar.vue";
import { useToast } from "@/composables/useToast";

const toast = useToast();

// TODO: CRUTCH REMOVE
const CRUTCH_HIDE_SECTION_ONLINE_PAYMENT = {
  artist: true,
};
const CRUTCH_HIDE_SECTION_SMS = {
  artist: true,
};
const showSectionOnlinePayment = ref(true);
const showSectionSms = ref(true);

const COMPANY_FALLBACK = {
  crmMode: null,
  localizationMode: null,
  spaSettings: {
    enabled: false,
  },
};

const router = useRouter();
const route = useRoute();
const { width } = useWindowSize();

const store = useStore();
const companyStore = useCompanyStore();

const props = defineProps({
  cardBg: {
    type: String,
  },
});

const sidenav = useTemplateRef("sidenav");
const isSwitchingInProgress = ref(false);

const showDevFeatures = ref(false);
const uiKitEnabled = process.env.VUE_APP_UI_KIT_ENABLED === "yes";
const originalShowDevFeatures = ref(false);
const gigaRoot = ref(false);
const company = computed(() => ({
  ...COMPANY_FALLBACK,
  ...companyStore.company,
}));

const currentTenant = ref(null);
const multiTenants = ref(false);
const availableTenants = ref([]);

const TENANT_STATUS_MAP = {
  trial: { label: "T", modifier: "trial" },
  trialEnded: { label: "F", modifier: "trial-ended" },
  waiting: { label: "W", modifier: "waiting" },
  subscribed: { label: "P", modifier: "paid" },
};

function getTenantStatusMeta(subscriptionStatus) {
  return TENANT_STATUS_MAP[subscriptionStatus] || null;
}

onMounted(async () => {
  showDevFeatures.value = allowedShowDevFeatures();

  originalShowDevFeatures.value = !!localStorage.getItem("showDevFeatures");

  const localUser = localStorage.getItem("localUser");
  try {
    const decodedUser = JSON.parse(localUser);
    if (decodedUser?.permissionsMap?.adminArea) {
      gigaRoot.value = true;
    }
  } catch (error) {
    // silenced
  }

  const result = await api.myprofile.getAvailableTenants();
  currentTenant.value = result.currentTenant;
  multiTenants.value = result.multiTenants;

  availableTenants.value = result.availableTenants.map((t) => ({
    ...t,
    searchField: `${t.name} ${t.id}`,
  }));

  if (result.currentTenant) {
    const found = availableTenants.value.find(
      (t) => t.id === result.currentTenant.id,
    );

    currentTenant.value = found || result.currentTenant;
  } else {
    currentTenant.value = null;
  }

  const tenantName = store.state.tenantName;
  if (CRUTCH_HIDE_SECTION_ONLINE_PAYMENT[tenantName]) {
    showSectionOnlinePayment.value = false;
  }
  if (CRUTCH_HIDE_SECTION_SMS[tenantName]) {
    showSectionSms.value = false;
  }
});

function isOwnerOrRoot() {
  return checkUserRole("owner") || checkUserRole("root");
}

function showHotelChessboards() {
  return (
    allowed("/ecommerce/calendar") ||
    allowed("/ecommerce/rooms-prices") ||
    allowed("/ecommerce/restrictions") ||
    (company.value.channelManager?.aiosell?.isActive &&
      allowed("/ecommerce/staff-rates"))
  );
}

function showSettingsSection() {
  return (
    allowed("/settings/company") ||
    allowed("/settings/staff") ||
    allowed("/settings/discounts") ||
    allowed("/settings/online-booking") ||
    allowed("/settings/online-payment") ||
    allowed("/settings/additional-services")
  );
}

function showTeamSection() {
  return (
    allowed("/team/managers") ||
    (company.value.crmMode !== "hotel" && allowed("/settings/staff"))
  );
}

function showIntegrationsSection() {
  return (
    allowed("/integrations/sms") ||
    allowed("/integrations/email") ||
    !checkUserRole("manager")
  );
}

function showManagementHeader() {
  return (
    allowed("/subscription") ||
    allowed("/ig-story-maker") ||
    showSettingsSection() ||
    showTeamSection() ||
    allowed("/channel-manager") ||
    showIntegrationsSection()
  );
}

function getRoute() {
  const routeArr = route.path.split("/");
  return routeArr[1];
}

function getFullRouteAsString() {
  return route.path;
}

async function switchTenant(tenant) {
  if (isSwitchingInProgress.value) {
    return;
  }
  isSwitchingInProgress.value = true;

  const tenantName = typeof tenant === "string" ? tenant : tenant?.name;

  try {
    // TODO: refactor to use `tenantId` for switching, not `tenantName`
    const result = await api.myprofile.switchTenant({
      tenantName,
    });

    if (result?.accessToken) {
      toast.success(`Перехід до "${tenantName}"...`);

      localStorage.setItem("switch-accessToken", result.accessToken);

      await router.push("/authentication/logout");
      isSwitchingInProgress.value = false;
    }
  } catch (error) {
    toast.error("Виникла помилка. Спробуйте ще раз");
    isSwitchingInProgress.value = false;
  }
}

onClickOutside(
  sidenav,
  () => {
    if (width.value < 1024) {
      closeSidebar();
    }
  },
  {
    ignore: ["#iconNavbarSidenav", ".sidebar-toggle"],
  },
);

function getLabelStaffFull() {
  const localizationMode = company.value.localizationMode;

  switch (true) {
    case localizationMode === "spa":
      return "Ресурси";
    case localizationMode === "hotel":
      return "Номери";
    default:
      return "Майстри";
  }
}

const uiStore = useUiStore();

function closeSidebar() {
  uiStore.isSidebarOpen = false;
}

router.beforeEach(() => {
  if (width.value < 1024) {
    closeSidebar();
  }
});
</script>
<template>
  <div
    id="sidenav-collapse-main"
    class="w-auto h-auto collapse navbar-collapse"
    ref="sidenav"
  >
    <ul class="navbar-nav">
      <li
        v-if="multiTenants && availableTenants.length > 1"
        :class="getRoute() === 'dashboards' ? 'active' : ''"
        class="nav-item"
      >
        <div class="px-3">
          <SelectApp
            v-model="currentTenant"
            :options="availableTenants"
            :allow-empty="false"
            :max-height="270"
            searchable
            placeholder="Select tenant"
            label-field="searchField"
            value-field="name"
            class="select-tenant"
            @select="switchTenant"
          >
            <template #singleLabel>
              <div class="d-flex align-items-center gap-2">
                <i class="material-icons-round collapse-icon">home</i>
                <span class="text-truncate">{{ currentTenant?.name }}</span>
              </div>
            </template>

            <template #option="{ option }">
              <div class="select-tenant__option">
                <span
                  v-if="getTenantStatusMeta(option.subscriptionStatus)"
                  class="select-tenant__status-badge"
                  :class="`select-tenant__status-badge--${getTenantStatusMeta(option.subscriptionStatus).modifier}`"
                >
                  {{ getTenantStatusMeta(option.subscriptionStatus).label }}
                </span>
                <span class="text-truncate select-tenant__option-label">{{
                  option.name
                }}</span>
              </div>
            </template>
          </SelectApp>
        </div>
      </li>
      <hr
        v-if="multiTenants && availableTenants.length > 1"
        class="horizontal light mt-2 mb-2"
      />

      <li
        v-if="allowed('/ecommerce/calendar')"
        :class="
          getFullRouteAsString() === '/ecommerce/calendar' ? 'active' : ''
        "
        class="nav-item"
      >
        <ul v-if="company.crmMode !== 'hotel'" class="nav pe-0">
          <!-- nav links -->
          <sidenav-item
            :class="
              getFullRouteAsString() === '/ecommerce/calendar' ? 'active' : ''
            "
            svg-icon="calendar_today"
            text="Календар"
            to="/ecommerce/calendar"
          />
        </ul>
      </li>
      <sidenav-item
        v-if="
          company.crmMode === 'hotel' &&
          showHotelChessboards() &&
          allowed('/dashboard')
        "
        svg-icon="dashboard"
        text="Dashboard"
        to="/dashboard"
      />
      <li
        v-if="company.crmMode === 'hotel' && showHotelChessboards()"
        :class="getRoute() === 'ecommerce' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'ecommerce' ? 'active' : ''"
          collapseRef="chessTable"
          navText="Шахматки"
        >
          <template v-slot:icon>
            <i class="material-icons-round opacity-10">calendar_today</i>
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                v-if="allowed('/ecommerce/calendar')"
                :nested="true"
                svg-icon="event_available"
                text="Бронювання"
                to="/ecommerce/calendar"
              />
              <sidenav-item
                v-if="allowed('/ecommerce/rooms-prices')"
                :nested="true"
                svg-icon="sell"
                text="Ціни"
                to="/ecommerce/rooms-prices"
              />
              <sidenav-item
                v-if="allowed('/ecommerce/restrictions')"
                :nested="true"
                svg-icon="free_cancellation"
                text="Обмеження"
                to="/ecommerce/restrictions"
              >
              </sidenav-item>
              <sidenav-item
                :nested="true"
                v-if="
                  company.crmMode === 'hotel' &&
                  company.channelManager?.aiosell?.isActive &&
                  allowed('/ecommerce/staff-rates')
                "
                svg-icon="sell"
                text="Канали"
                to="/ecommerce/staff-rates"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <li
        v-if="allowed('/service-orders') && company.crmMode !== 'hotel'"
        class="nav-item"
      >
        <ul class="nav pe-0">
          <sidenav-item
            :class="
              getFullRouteAsString() === '/service-orders' ? 'active' : ''
            "
            svg-icon="playlist_add_check"
            text="Записи"
            to="/service-orders"
          />
        </ul>
      </li>
      <li
        v-if="allowed('/service-orders') && company.crmMode === 'hotel'"
        class="nav-item"
      >
        <ul class="nav pe-0">
          <sidenav-item
            :class="
              getFullRouteAsString() === '/service-orders' ? 'active' : ''
            "
            text="Броні"
            to="/service-orders"
          >
            <template #icon>
              <CalendarCheck />
            </template>
          </sidenav-item>
        </ul>
      </li>
      <li v-if="allowed('/clients')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            :class="getFullRouteAsString() === '/clients' ? 'active' : ''"
            text="Клієнти"
            to="/clients"
          >
            <template #icon><Clients /></template>
          </sidenav-item>
        </ul>
      </li>
      <li
        v-if="allowed('/sellable-services')"
        :class="getRoute() === 'sellable-services' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'sellable-services' ? 'active' : ''"
          collapseRef="productsServicesTree"
          navText="Товари і послуги"
        >
          <template v-slot:icon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              width="18"
              height="18"
              class="opacity-10"
              aria-label="booking.com"
              style="fill: #fff"
            >
              <path
                d="M504 416h-24v-64c0-118.31-91.79-214.96-208-223.19V96h40c4.42 0 8-3.58 8-8V72c0-4.42-3.58-8-8-8H200c-4.42 0-8 3.58-8 8v16c0 4.42 3.58 8 8 8h40v32.81C123.79 137.04 32 233.69 32 352v64H8c-4.42 0-8 3.58-8 8v16c0 4.42 3.58 8 8 8h496c4.42 0 8-3.58 8-8v-16c0-4.42-3.58-8-8-8zm-56 0H64v-64c0-100.17 78.3-184.19 178.26-191.27 16.87-1.19 10.33-1.21 27.48 0C369.7 167.81 448 251.83 448 352v64z"
              />
            </svg>
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                :nested="true"
                miniIcon="П"
                text="Продажі"
                to="/sellable-services/sales"
              />
              <sidenav-item
                :nested="true"
                miniIcon="Т"
                text="Товари і послуги"
                to="/sellable-services"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <li
        v-if="allowed('/analytics')"
        :class="getRoute() === 'analytics' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'analytics' ? 'active' : ''"
          collapseRef="analyticsTree"
          navText="Аналітика"
        >
          <template #icon>
            <ChartBar />
          </template>

          <template #list>
            <ul class="nav pe-0">
              <sidenav-item
                :class="getFullRouteAsString() === '/analytics' ? 'active' : ''"
                :nested="true"
                svg-icon="analytics"
                text="Загальні показники"
                to="/analytics"
              />
              <sidenav-item
                v-if="company.crmMode === 'hotel'"
                :nested="true"
                svg-icon="analytics"
                text="Завантаженість"
                to="/analytics/bystaff"
              />
              <sidenav-item
                v-if="company.crmMode === 'hotel'"
                :nested="true"
                svg-icon="analytics"
                text="ADR"
                to="/analytics/adr"
              />
              <sidenav-item
                v-if="company.crmMode !== 'hotel'"
                :nested="true"
                svg-icon="analytics"
                text="За майстрами"
                to="/analytics/bystaff"
              />
              <sidenav-item
                v-if="isOwnerOrRoot()"
                :nested="true"
                svg-icon="analytics"
                text="За адміністраторами"
                to="/analytics/byadministrators"
              />
              <sidenav-item
                :nested="true"
                svg-icon="analytics"
                text="За дод. послугами"
                to="/analytics/byadditionalservices"
              />
              <sidenav-item
                v-if="showDevFeatures"
                :nested="true"
                svg-icon="analytics"
                text="DEV За послугами"
                to="/analytics/byservices"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <li
        class="nav-item"
        :class="getRoute() === 'reviews' ? 'active' : ''"
        v-if="allowed('/reviews')"
      >
        <sidenav-collapse
          collapseRef="analyticsTree"
          navText="Відгуки"
          :class="getRoute() === 'reviews' ? 'active' : ''"
        >
          <template v-slot:icon>
            <i class="material-icons-round opacity-10">reviews</i>
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                to="/reviews/reviews-overview"
                :nested="true"
                text="Відгуки клієнтів"
                :class="getFullRouteAsString() === '/reviews' ? 'active' : ''"
              />
              <sidenav-item
                :nested="true"
                to="/reviews/reviews-settings"
                svg-icon="reviews"
                text="Налаштування"
                :class="getFullRouteAsString() === '/reviews' ? 'active' : ''"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <!-- <li v-if="showDevFeatures && allowed('/pages/journal')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            svg-icon="subject"
            text="Журнал DEV"
            to="/pages/journal"
          />
        </ul>
      </li> -->
      <li v-if="allowed('/finances/cash')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item text="P&L" to="/finances/cash">
            <template #icon> <SackDollar /></template>
          </sidenav-item>
        </ul>
      </li>
      <li
        v-if="company.spaSettings.enabled && allowed('/spa')"
        class="nav-item"
        :class="getRoute() === 'spa' ? 'active' : ''"
      >
        <sidenav-collapse
          collapseRef="spaTree"
          navText="SPA"
          :class="getRoute() === 'spa' ? 'active' : ''"
        >
          <template #label> SPA </template>
          <template #icon>
            <Spa />
          </template>
          <template #list>
            <ul class="nav pe-0">
              <sidenav-item :nested="true" text="Календар" to="/spa/calendar" />
              <sidenav-item :nested="true" text="Записи" to="/spa/orders" />
              <sidenav-item :nested="true" text="Ресурси" to="/spa/resources" />
              <sidenav-item
                :nested="true"
                text="Графік ресурсів"
                to="/spa/resources-schedule"
              />
              <sidenav-item :nested="true" text="Послуги" to="/spa/services" />
              <sidenav-item
                :nested="true"
                text="Онлайн запис"
                to="/spa/online-booking"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <li
        v-if="company.crmMode === 'hotel' && allowed('/promocodes')"
        class="nav-item"
        :class="getRoute() === 'promocodes' ? 'active' : ''"
      >
        <sidenav-collapse
          collapseRef="promocodesTree"
          navText="Промокоди"
          :class="getRoute() === 'promocodes' ? 'active' : ''"
        >
          <!-- <template #label>
            Промокоди
            <span class="badge bg-gradient-info" style="margin-left: 5px"
              >NEW</span
            >
          </template> -->
          <template v-slot:icon>
            <i class="material-icons-round opacity-10">sell</i>
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                to="/promocodes/promocodes-overview"
                :nested="true"
                text="Мої промокоди"
              />
              <sidenav-item
                :nested="true"
                to="/promocodes/promocodes-history"
                text="Історія використання"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <!-- no priority -->
      <!-- <li v-if="showDevFeatures" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            :class="getFullRouteAsString() === '/certificates' ? 'active' : ''"
            svg-icon="redeem"
            text="Сертифікати DEV"
            to="/certificates"
          />
        </ul>
      </li> -->
      <!-- no priority -->
      <!-- <li v-if="showDevFeatures && allowed('/abonements')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            svg-icon="badge"
            text="Абонементи DEV"
            to="/abonements"
          />
        </ul>
      </li> -->

      <!-- no priority -->
      <!-- <li
        v-if="showDevFeatures && allowed('/warehouse')"
        :class="getRoute() === 'warehouse' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'warehouse' ? 'active' : ''"
          collapseRef="warehouseTree"
          navText="Склад DEV"
        >
          <template v-slot:icon>
            <i class="material-icons-round opacity-10">warehouse</i>
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                :nested="true"
                miniIcon="П"
                text="Продажі"
                to="/warehouse/product-orders"
              />
              <sidenav-item
                :nested="true"
                miniIcon="Т"
                text="Товари"
                to="/warehouse/products-list"
              />
              <sidenav-item
                :nested="true"
                miniIcon="П"
                text="Постачальники"
                to="/warehouse/suppliers"
              />
              <sidenav-item
                :nested="true"
                miniIcon="П"
                text="Поставки"
                to="/warehouse/suppliesdatatable"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li> -->
      <li v-if="showManagementHeader()" class="mt-3 nav-item">
        <h6
          :class="store.state.isRTL ? 'me-4' : 'ms-2'"
          class="text-xs ps-4 text-uppercase font-weight-bolder text-white"
        >
          Керування
        </h6>
      </li>
      <li v-if="allowed('/subscription')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            :class="getFullRouteAsString() === '/subsctiption' ? 'active' : ''"
            svg-icon="subscriptions"
            text="Підписка і рахунки"
            to="/subscription"
            :badge="companyStore.pendingInvoicesCount"
          />
        </ul>
      </li>
      <li v-if="allowed('/ig-story-maker')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            v-if="company.crmMode === 'hotel'"
            :icon="true"
            text="IG Stories maker"
            to="/ig-story-maker"
          >
            <template #icon>
              <Instagram />
            </template>
          </sidenav-item>
        </ul>
      </li>

      <li
        v-if="showSettingsSection()"
        :class="getRoute() === 'settings' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'settings' ? 'active' : ''"
          collapseRef="settingsTree"
          navText="Налаштування"
        >
          <template v-slot:icon>
            <i class="material-icons-round opacity-10">settings</i>
          </template>

          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                v-if="allowed('/settings/company')"
                :nested="true"
                svg-icon="apartment"
                text="Моя компанія"
                to="/settings/company"
              />
              <sidenav-item
                v-if="company.crmMode === 'hotel' && allowed('/settings/staff')"
                :nested="true"
                svg-icon="meeting_room"
                text="Номери"
                to="/settings/staff"
              />
              <sidenav-item
                v-if="
                  company.crmMode === 'hotel' && allowed('/settings/discounts')
                "
                :nested="true"
                svg-icon="percent"
                text="Знижки"
                to="/settings/discounts"
              />

              <sidenav-item
                v-if="
                  company.crmMode !== 'hotel' &&
                  allowed('/settings/online-booking')
                "
                :nested="true"
                svg-icon="computer"
                text="Онлайн запис"
                to="/settings/online-booking"
              />
              <sidenav-item
                v-if="
                  company.crmMode === 'hotel' &&
                  allowed('/settings/online-booking')
                "
                :nested="true"
                svg-icon="computer"
                text="Онлайн бронь"
                to="/settings/online-booking"
              />
              <sidenav-item
                v-if="
                  showSectionOnlinePayment &&
                  allowed('/settings/online-payment')
                "
                :nested="true"
                svg-icon="computer"
                text="Онлайн оплата"
                to="/settings/online-payment"
              />
              <sidenav-item
                v-if="
                  company.crmMode === 'hotel' &&
                  allowed('/settings/online-checkin')
                "
                :nested="true"
                svg-icon="how_to_reg"
                text="Онлайн check-in"
                to="/settings/online-checkin"
              >
              </sidenav-item>
              <sidenav-item
                v-if="company.crmMode !== 'hotel'"
                :nested="true"
                svg-icon="volunteer_activism"
                text="Послуги"
                to="/settings/services"
              />
              <sidenav-item
                v-if="allowed('/settings/additional-services')"
                :nested="true"
                svg-icon="volunteer_activism"
                text="Додаткові послуги"
                to="/settings/additional-services"
              />
              <!-- no priority -->
              <!-- <sidenav-item
                v-if="showDevFeatures"
                :nested="true"
                svg-icon="key"
                text="DEV Доступи"
                to="/settings/permissions"
              /> -->
            </ul>
          </template>
        </sidenav-collapse>
      </li>

      <li
        v-if="showTeamSection()"
        :class="getRoute() === 'team' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'team' ? 'active' : ''"
          collapseRef="teamTree"
          navText="Команда"
        >
          <template v-slot:icon>
            <i class="material-icons-round opacity-10">people</i>
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                v-if="allowed('/team/managers')"
                :icon="true"
                :nested="true"
                text="Адміністратори"
                to="/team/managers"
              >
                <template #icon>
                  <img :src="Administrator" alt="" />
                </template>
              </sidenav-item>
              <sidenav-item
                v-if="company.crmMode !== 'hotel' && allowed('/settings/staff')"
                :nested="true"
                :text="getLabelStaffFull()"
                svg-icon="person"
                to="/settings/staff"
              />
              <sidenav-item
                v-if="company.crmMode !== 'hotel' && allowed('/settings/staff')"
                :nested="true"
                svg-icon="edit_calendar"
                text="Графік роботи"
                to="/team/workschedule"
              />

              <!-- no priority -->
              <!-- <sidenav-item
                v-if="showDevFeatures"
                :icon="true"
                :nested="true"
                text="Зарплатня DEV"
                to="/team/salary"
              >
                <template #icon>
                  <img :src="Salary" alt="" />
                </template>
              </sidenav-item> -->
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <li v-if="allowed('/channel-manager')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            v-if="company.crmMode === 'hotel'"
            svg-icon="share"
            text="Менеджер каналів"
            to="/channel-manager"
          />
        </ul>
      </li>

      <li
        v-if="showIntegrationsSection()"
        :class="getRoute() === 'integrations' ? 'active' : ''"
        class="nav-item"
      >
        <sidenav-collapse
          :class="getRoute() === 'integrations' ? 'active' : ''"
          collapseRef="settingsTree"
          navText="Інтеграції"
        >
          <template v-slot:icon>
            <i class="material-icons-round opacity-10"
              >integration_instructions</i
            >
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                v-if="company.crmMode === 'hotel'"
                :icon="true"
                :nested="true"
                text="POSter (beta)"
                to="/integrations/poster"
              >
                <template #icon>
                  <img :src="telegram" alt="" />
                </template>
              </sidenav-item>
              <sidenav-item
                :icon="true"
                :nested="true"
                text="Telegram bot"
                to="/integrations/telegram"
              >
                <template #icon>
                  <img :src="telegram" alt="" />
                </template>
              </sidenav-item>
              <sidenav-item
                v-if="showSectionSms"
                v-show="allowed('/integrations/sms')"
                :nested="true"
                svg-icon="sms"
                text="SMS"
                to="/integrations/sms"
              />
              <sidenav-item
                v-if="allowed('/integrations/email')"
                :nested="true"
                text="Email комунікація"
                to="/integrations/email"
              />
              <sidenav-item
                :nested="true"
                svg-icon="widgets"
                text="Віджет на сайт"
                to="/integrations/widget"
              />
              <sidenav-item
                :nested="true"
                svg-icon="code"
                text="API"
                to="/integrations/api"
              />
              <sidenav-item
                :nested="true"
                text="АІ агент"
                to="/integrations/ai-agent"
              >
                <template #label>
                  <span class="badge bg-gradient-info" style="margin-left: 5px"
                    >NEW</span
                  >
                </template>
              </sidenav-item>
            </ul>
          </template>
        </sidenav-collapse>
      </li>
      <li v-if="allowed('/support')" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            svg-icon="support_agent"
            text="Допомога"
            to="/support"
          />
        </ul>
      </li>
      <li v-if="uiKitEnabled" class="nav-item">
        <ul class="nav pe-0">
          <sidenav-item
            :class="getRoute() === 'ui-kit' ? 'active' : ''"
            svg-icon="widgets"
            text="UI Kit"
            to="/ui-kit"
          />
        </ul>
      </li>
      <hr />
      <li class="mt-3 nav-item">
        <h6
          :class="store.state.isRTL ? 'me-4' : 'ms-2'"
          class="text-xs ps-4 text-uppercase font-weight-bolder text-white"
        >
          Мобільні застосунки
        </h6>
        <div class="d-flex justify-content-start align-items-center ps-4">
          <a
            href="https://apps.apple.com/ua/app/bookmenow/id6444885822"
            target="_blank"
            class="me-2"
          >
            <img src="@/assets/img/AppStore.svg" alt="App Store" width="102" />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.app.bookmenow"
            target="_blank"
          >
            <img
              src="@/assets/img/GooglePlay.svg"
              alt="Google Play"
              width="102"
            />
          </a>
        </div>
      </li>

      <li
        v-if="allowed('/admin-area') && originalShowDevFeatures && gigaRoot"
        :class="getRoute() === 'admin-area' ? 'active' : ''"
        class="nav-item"
        style="color: #f44335 !important"
      >
        <sidenav-collapse
          :class="getRoute() === 'maintenance' ? 'active' : ''"
          class="text-danger"
          collapseRef="settingsTree"
          navText="Admin area"
          style="color: #f44335 !important"
        >
          <template v-slot:icon>
            <i
              class="material-icons-round opacity-10"
              style="color: #f44335 !important"
              >lock</i
            >
          </template>
          <template v-slot:list>
            <ul class="nav pe-0">
              <sidenav-item
                miniIcon="M"
                text="migrations"
                to="/maintenance/migrations"
              />
              <sidenav-item miniIcon="C" text="cron" to="/maintenance/cron" />
              <sidenav-item
                miniIcon="T"
                text="tenants"
                to="/maintenance/tenants"
              />
              <sidenav-item
                miniIcon="T"
                text="tenants linking"
                to="/maintenance/tenants-linking"
              />
            </ul>
          </template>
        </sidenav-collapse>
      </li>
    </ul>
  </div>
</template>
<style scoped>
.select-tenant {
  --select-app-hover-bg: rgba(255, 255, 255, 0.08);
}
:deep(.select-tenant .multiselect__tags) {
  padding-right: 34px;
  padding-left: 16px;
  background: transparent;
  border: 0.5px solid transparent;
  min-height: 50px;
}
:deep(.select-tenant.multiselect--active .multiselect__tags) {
  border-color: transparent;
  box-shadow: none;
}
:deep(.select-tenant .multiselect__select) {
  background: url("~@/assets/img/HiChevronDown-white.svg") center / cover
    no-repeat;
}
:deep(.select-tenant .multiselect__single) {
  background: transparent;
  color: #fff;
}
:deep(.select-tenant .multiselect__content-wrapper) {
}

.select-tenant__option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
}

.select-tenant__option-label {
  min-width: 0;
  flex: 1 1 auto;
}

.select-tenant__status-badge {
  min-width: 32px;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-family: var(--font-manrope);
  font-weight: 700;
  font-size: 16px;
  line-height: 1;
}

.select-tenant__status-badge--trial {
  color: #0f73d8;
  background: #d9efff;
}

.select-tenant__status-badge--trial-ended {
  color: #c27b1f;
  background: #ffe3bc;
}

.select-tenant__status-badge--waiting {
  color: #c5392f;
  background: #ffe0dc;
}

.select-tenant__status-badge--paid {
  color: #2d7b46;
  background: #dcefdc;
}
</style>
